import { deiterateAPI } from '@/js/external-js/variableModules/utils/deiterateAPI.util';

const SIG_API = '/v1/special-interest-groups';

export interface SpecialInterestGroup {
  id?: string;
  groupName: string;
  primaryContact: string;
  contactEmail: string;
  contactPhone: string;
  internalOwner: string;
  comments?: string;
  addedBy: string;
  updatedBy: string;
  dateAdded: Date | string;
  lastUpdated: Date | string;
}

// Helper to get headers
function getHeaders(tenantId: string) {
  return {
    'X-Tenancy-Id': tenantId,
    'Company-ID': tenantId,
  };
}

// Helper to parse group from API response
function parseGroup(group: any): SpecialInterestGroup {
  return {
    id: group.id || '',
    groupName: group.groupName || '',
    primaryContact: group.primaryContact || '',
    contactEmail: group.contactEmail || '',
    contactPhone: group.contactPhone || '',
    internalOwner: group.internalOwner || '',
    comments: group.comments || '',
    addedBy: group.addedBy || '',
    updatedBy: group.updatedBy || '',
    dateAdded: group.dateAdded || new Date().toISOString(),
    lastUpdated: group.lastUpdated || new Date().toISOString(),
  };
}

/**
 * Fetches all special interest groups for a company via Thalassa API
 * @param tenantId - The tenant ID
 * @returns Promise with the special interest groups array
 */
export async function getSpecialInterestGroups(tenantId: string): Promise<SpecialInterestGroup[]> {
  console.log('[specialInterestGroupsService] getSpecialInterestGroups called with:', tenantId);
  
  try {
    const response = await deiterateAPI.GET(SIG_API, {
      mode: 'cors',
      backend: 'thalassa',
      headers: getHeaders(tenantId),
    });

    if (response.statusCode === 200 && response.groups) {
      return response.groups.map(parseGroup);
    }
    
    return [];
  } catch (error) {
    console.error('[specialInterestGroupsService] Error getting special interest groups:', error);
    throw error;
  }
}

/**
 * Creates a new special interest group via Thalassa API
 * @param tenantId - The tenant ID
 * @param data - The special interest group data to create
 * @returns Promise that resolves when the group is created
 */
export async function createSpecialInterestGroup(
  tenantId: string, 
  data: Omit<SpecialInterestGroup, 'id' | 'addedBy' | 'updatedBy' | 'dateAdded' | 'lastUpdated'>
): Promise<void> {
  console.log('[specialInterestGroupsService] createSpecialInterestGroup called with:', { tenantId, data });
  
  try {
    const response = await deiterateAPI.POST(SIG_API, {
      mode: 'cors',
      backend: 'thalassa',
      headers: getHeaders(tenantId),
      body: JSON.stringify(data),
    });

    if (response.statusCode !== 200) {
      throw new Error(response.error || 'Failed to create special interest group');
    }
  } catch (error) {
    console.error('[specialInterestGroupsService] Error creating special interest group:', error);
    throw error;
  }
}

/**
 * Updates an existing special interest group via Thalassa API
 * @param tenantId - The tenant ID
 * @param id - The ID of the group to update
 * @param updates - The updated group data
 * @returns Promise that resolves when the group is updated
 */
export async function updateSpecialInterestGroup(
  tenantId: string, 
  id: string, 
  updates: Partial<SpecialInterestGroup>
): Promise<void> {
  console.log('[specialInterestGroupsService] updateSpecialInterestGroup called with:', { tenantId, id, updates });
  
  try {
    const response = await deiterateAPI.PUT(`${SIG_API}/${id}`, {
      mode: 'cors',
      backend: 'thalassa',
      headers: getHeaders(tenantId),
      body: JSON.stringify(updates),
    });

    if (response.statusCode !== 200) {
      throw new Error(response.error || 'Failed to update special interest group');
    }
  } catch (error) {
    console.error('[specialInterestGroupsService] Error updating special interest group:', error);
    throw error;
  }
}

/**
 * Deletes a special interest group via Thalassa API
 * @param tenantId - The tenant ID
 * @param id - The ID of the group to delete
 * @returns Promise that resolves when the group is deleted
 */
export async function deleteSpecialInterestGroup(tenantId: string, id: string): Promise<void> {
  console.log('[specialInterestGroupsService] deleteSpecialInterestGroup called with:', { tenantId, id });
  
  try {
    const response = await deiterateAPI.DELETE(`${SIG_API}/${id}`, {
      mode: 'cors',
      backend: 'thalassa',
      headers: getHeaders(tenantId),
    });

    if (response.statusCode !== 200) {
      throw new Error(response.error || 'Failed to delete special interest group');
    }
  } catch (error) {
    console.error('[specialInterestGroupsService] Error deleting special interest group:', error);
    throw error;
  }
}

export default {
  getSpecialInterestGroups,
  createSpecialInterestGroup,
  updateSpecialInterestGroup,
  deleteSpecialInterestGroup,
};
